import React from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import useRoutes from "./routes";
import Sidebar from "Layout/VerticalLayout/Sidebar";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import Layout from "Layout";
import { useUserInfo } from "context/user";

interface RouteItem {
  id: string;
  path?: string;
  title?: string;
  icon?: React.ReactNode;
  checkPermission?: boolean;
  component?: React.ReactNode;
  subItems?: RouteItem[];
}

export default function Default() {
  const routes = useRoutes();
  const { user } = useUserInfo();

  const selectLayoutState = (state: any) => state.Layout;
  const selectLayoutProperties = createSelector(
    selectLayoutState,
    (layout) => ({
      layoutType: layout.layoutType,
      layoutSidebarSizeType: layout.layoutSidebarSizeType,
    })
  );

  const { layoutType, layoutSidebarSizeType } = useSelector(selectLayoutProperties);

  const userPermissions = user?.permissions || [];
  const isCompanyAdmin = user?.userType=="companyAdmin";

  /** 🔹 Filter routes recursively based on permissions */
  const filterRoutesByPermission = (routes: RouteItem[]): RouteItem[] => {
    return routes
      .filter(
        (r) =>
          !r.checkPermission ||
          isCompanyAdmin ||
          userPermissions.includes(r.title || "")
      )
      .map((r) => ({
        ...r,
        subItems: r.subItems ? filterRoutesByPermission(r.subItems) : undefined,
      }));
  };

  const accessibleMenus = isCompanyAdmin
    ? routes
    : filterRoutesByPermission(routes);

  /** 🔹 Find first allowed route for redirect */
  const findFirstAllowedRoute = (routes: RouteItem[]): RouteItem | undefined => {
    for (const route of routes) {
      if (route.path && (!route.checkPermission || isCompanyAdmin || userPermissions.includes(route.title || ""))) {
        return route;
      }
      if (route.subItems?.length) {
        const found = findFirstAllowedRoute(route.subItems);
        if (found) return found;
      }
    }
    return undefined;
  };

  const firstAllowedRoute = findFirstAllowedRoute(accessibleMenus);

  /** 🔹 Recursively generate all routes with parent path prefix */
  const generateRoutes = (
    routeList: RouteItem[],
    parentPath = ""
  ): React.ReactNode[] => {
    return routeList.flatMap((route) => {
      const fullPath = route.path
        ? `${parentPath}${route.path.startsWith("/") ? route.path : `/${route.path}`}`
        : parentPath;

      const isAllowed =
        !route.checkPermission ||
        isCompanyAdmin ||
        userPermissions.includes(route.title || "");

      const elements: React.ReactNode[] = [];

      if (route.component) {
        elements.push(
          <Route
            key={fullPath}
            path={fullPath}
            element={
              isAllowed ? (
                route.component
              ) : (
                <Navigate to={firstAllowedRoute?.path || "/"} replace />
              )
            }
          />
        );
      }

      if (route.subItems?.length) {
        elements.push(...generateRoutes(route.subItems, fullPath));
      }

      return elements;
    });
  };

  return (
    <>
      <Sidebar
        menus={accessibleMenus}
        layoutType={layoutType}
        layoutSidebarSizeType={layoutSidebarSizeType}
      />

      <Layout>
        <Routes>{generateRoutes(routes)}</Routes>
      </Layout>
    </>
  );
}
